import * as PIXI from 'pixi.js';

import { AssetCreatedFrom } from '../../../enums';
import type { SeekMode } from '../../../enums';
import type { Transformation as TransformationType } from '../../../types';
import { Config } from '../../../video-data/config';
import { getPositionFromTransformation } from '../../../video-data/designs/scenes';
import type { LoadSettings } from '../../serializableMixin';
import { DynamicStylingMixin } from '../../dynamicStylingMixin';
import type { BaseAssetSettings } from '../BaseAsset';
import { BaseAsset } from '../BaseAsset';
import { resetSprite } from '../utils';
import { StringAttribute } from '../../attributes';
import { ChartRenderer } from './ChartRenderer';
import { deserializeFunctions, serializeFunctions } from './function-serialization';
import type { ChartConfig, ChartPalette, ChartSeriesType } from './types';

type ChartAssetSettings = BaseAssetSettings;

export class ChartAsset extends DynamicStylingMixin(BaseAsset) {
    override _classType = Config.CHART_ASSET_TYPE;

    #width = 800;
    #height = 600;
    #config: ChartConfig | null = null;
    #resolvedConfig: ChartConfig | null = null;
    #progress = 1;
    #createdFrom: AssetCreatedFrom = AssetCreatedFrom.SCENE_DESIGN;

    #sprite: PIXI.Sprite;
    #renderer: ChartRenderer;

    #resolvedSource: { rawConfig: ChartConfig; paletteKey: string } | null = null;

    constructor(container, settings: ChartAssetSettings) {
        super(container, settings);

        this.#renderer = new ChartRenderer();
        this.#sprite = new PIXI.Sprite();
        this._addPixiChild(this.#sprite);
        this.#createdFrom = settings.createdFrom ?? this.#createdFrom;
    }

    get createdFrom(): AssetCreatedFrom {
        return this.#createdFrom;
    }

    _createdFromCustomLoad(value: AssetCreatedFrom) {
        this.#createdFrom = value;
    }

    override destroy() {
        this.#renderer.destroy();
        super.destroy(false);
        this.#sprite.destroy(false);
    }

    override get failedToLoad() {
        return false;
    }

    override get isLoaded() {
        return true;
    }

    override get _shouldLoad() {
        return true;
    }

    override get width() {
        return this.#width;
    }

    override set width(value: number) {
        if (this.#width === value) {
            return;
        }
        this.#width = value;
        this.requiresRedraw = true;
    }

    override get height() {
        return this.#height;
    }

    override set height(value: number) {
        if (this.#height === value) {
            return;
        }
        this.#height = value;
        this.requiresRedraw = true;
    }

    get maxWidth() {
        return this.#width;
    }

    set maxWidth(value: number) {
        this.width = value;
    }

    get maxHeight() {
        return this.#height;
    }

    set maxHeight(value: number) {
        this.height = value;
    }

    getVisibleResolution(): { width: number; height: number } {
        return { width: this.#width, height: this.#height };
    }

    override get approximateMaxBounds(): PIXI.Rectangle {
        const freeParent = this.getFreelyPositionedParent();
        const transformation = freeParent.transformation;
        const { width, height } = this.getVisibleResolution();
        const { x, y } = getPositionFromTransformation(transformation, width, height);
        return { x, y, width, height } as PIXI.Rectangle;
    }

    override applyTransformation(offsetX = 0, offsetY = 0, offsetRotation = 0) {
        if (this.isPositionControlledByParent) {
            return;
        }
        const { width, height } = this.getVisibleResolution();
        const { x, y } = getPositionFromTransformation(
            this.transformation.save() as TransformationType,
            width,
            height,
        );

        this._pixiContainer.x = x + offsetX;
        this._pixiContainer.y = y + offsetY;
        this._pixiContainer.rotation = this.transformation.rotation + offsetRotation;
    }

    get config(): ChartConfig | null {
        return this.#config;
    }

    set config(value: ChartConfig | null) {
        if (this.#config === value) {
            return;
        }
        this.#config = value;
        this.#resolvedSource = null;
        this.requiresRedraw = true;
    }

    applyColorPermutation(permutation: number[]): void {
        if (!this.#config) {
            return;
        }
        const remap = (node: unknown): unknown => {
            if (typeof node === 'string') {
                const match = node.match(/^themeRef_colors_(\d+)$/);
                if (!match) return node;
                const oldIndex = parseInt(match[1], 10);
                const newIndex = permutation[oldIndex];
                if (typeof newIndex !== 'number' || newIndex === oldIndex) return node;
                return `themeRef_colors_${newIndex}`;
            }
            if (Array.isArray(node)) {
                return node.map(remap);
            }
            if (node && typeof node === 'object') {
                const result: Record<string, unknown> = {};
                for (const key in node as Record<string, unknown>) {
                    result[key] = remap((node as Record<string, unknown>)[key]);
                }
                return result;
            }
            return node;
        };
        this.config = remap(this.#config) as ChartConfig;
    }

    @StringAttribute()
    accessor chartType: ChartSeriesType | '' = '';

    @StringAttribute()
    accessor chartData = '';

    @StringAttribute()
    accessor chartPrompt = '';

    #getPalette(): ChartPalette {
        const colors: string[] = [];
        if (this.template?.colors) {
            for (const templateColor of this.template.colors) {
                const hexColor = '#' + templateColor.color.toString(16).padStart(6, '0');
                colors.push(hexColor);
            }
        }
        return {
            colors,
            background: 'transparent',
            text: '#8b949e',
            axis: '#30363d',
            grid: '#21262d',
        };
    }

    override _hasTemplateReferenceValues(): boolean {
        if (!this.#config) {
            return false;
        }
        return true;
    }

    override updateForRender() {
        if (!this.visible || !this.#resolvedConfig) {
            return;
        }

        this.#sprite.texture = this.root._reusableTextures.allocateTexture(() => {
            resetSprite(this.#sprite);
        });

        const canvas = (this.#sprite.texture.baseTexture.resource as PIXI.BaseImageResource)
            .source as HTMLCanvasElement;

        const ctx = canvas.getContext('2d');
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
        ctx?.resetTransform();
        this.#renderer.renderToCanvas(canvas, this.#resolvedConfig, this.#progress);

        this.#sprite.width = this.#width;
        this.#sprite.height = this.#height;

        this.#sprite.texture.update();
    }

    #resolveThemeRef(value: unknown, palette: ChartPalette): unknown {
        if (typeof value !== 'string') return value;

        const colorMatch = value.match(/^themeRef_colors_(\d+)$/);
        if (colorMatch && palette?.colors) {
            const index = parseInt(colorMatch[1], 10);
            return palette.colors[index] || value;
        }

        const refMatch = value.match(/^themeRef_(\w+)$/);
        if (refMatch && palette) {
            const key = refMatch[1] as keyof ChartPalette;
            return palette[key] || value;
        }

        return value;
    }

    #refreshResolvedConfig() {
        if (!this.#config) {
            this.#resolvedConfig = null;
            this.#resolvedSource = null;
            return;
        }
        const palette = this.#getPalette();
        const paletteKey = palette.colors.join('|');
        const cached = this.#resolvedSource;
        if (cached && cached.rawConfig === this.#config && cached.paletteKey === paletteKey) {
            return;
        }
        const deserialized = deserializeFunctions(this.#config) as ChartConfig;
        this.#resolvedConfig = {
            ...deserialized,
            echarts: this.#resolveAllRefs(deserialized.echarts, palette),
        };
        this.#resolvedSource = { rawConfig: this.#config, paletteKey };
    }

    #resolveAllRefs<T>(obj: T, palette: ChartPalette): T {
        if (obj === null || obj === undefined) return obj;

        if (typeof obj === 'string') {
            return this.#resolveThemeRef(obj, palette) as T;
        }

        if (Array.isArray(obj)) {
            return obj.map((item) => this.#resolveAllRefs(item, palette)) as T;
        }

        if (typeof obj === 'object') {
            const result: Record<string, unknown> = {};
            for (const key in obj) {
                result[key] = this.#resolveAllRefs((obj as Record<string, unknown>)[key], palette);
            }
            return result as T;
        }

        return obj;
    }

    override _build() {
        this.clearAnimation();

        this.#refreshResolvedConfig();

        super._build();

        if (this.hasDynamicStyle || this.hasMorphAnimation) {
            this._prepareDynamicStylingForUpdate();
            this.applyTransformation();
            this._applyAnimation();
            this._updateDynamicStyling();
        } else {
            this.applyTransformation();
            this._applyAnimation();
        }

        this._applyIntroAnimations();
        this._applyOutroAnimations();
        this.animationManager.setInitValues();
    }

    override _draw() {
        if (!this.#config) {
            return;
        }

        this.#sprite.width = this.#width;
        this.#sprite.height = this.#height;
    }

    override _seek(progress: number, seekMode: SeekMode) {
        super._seek(progress, seekMode);

        if (!this.#config) return;

        const animationDuration = 1500;
        const sceneDuration = this.container?.duration ?? animationDuration;
        const animationFraction = Math.min(1, animationDuration / sceneDuration);
        const rawChartProgress = Math.min(1, Math.max(0, progress) / animationFraction);
        const quantized = Math.round(rawChartProgress * 50) / 50;

        this.#progress = Math.max(0, Math.min(1, quantized));
    }

    _configCustomSave() {
        return this.#config ? serializeFunctions(this.#config) : null;
    }

    _configCustomLoad(configData: ChartConfig | null, _settings: LoadSettings) {
        this.#config = configData;
        this.#resolvedSource = null;
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            'mask',
            'maxWidth',
            'maxHeight',
            'config',
            'chartType',
            'chartData',
            'chartPrompt',
            'createdFrom',
        ]);
    }
}
